import React from 'react';
import { func, any, bool } from 'prop-types';
import numeral from 'numeral';
import Radium from 'radium';
import { UTLabel, WithLoading, UTLoading } from '@widergy/energy-ui';

import withErrorScreen from 'app/components/UTErrorScreen/HOCs/withErrorScreen';
import UTTable from 'app/components/UTTable';
import { BILLS } from 'constants/common';
import { billsActiveRowSize as activeRowSize, inactiveRowSize } from 'constants/sizes';
import UTDownloadBill from 'app/components/UTDownloadBill';
import { DefaultViewport, MobileViewport } from 'constants/deviceSizes';
import { appConfig } from 'config/customization';
import { message } from 'utils/stringUtils';

import tableStyles from '../Claims/styles';

import BillDetails from './components/BillDetails';
import BillsMobileTable from './components/BillsMobileTable';
import stylesJS from './styles';
import styles from './styles.module.scss';

/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */

const downloadButtonRenderer = onClickDownload => ({ cellData }) => (
  <div style={stylesJS.downloadButtonContainer}>
    <UTDownloadBill
      loadingMessage={message('Bill:loadingBill')}
      title={message('Bills:bill')}
      downloadUrl={cellData.url}
      useIcon={appConfig.bills.downloadIconBillsScreen}
      labelButton={!appConfig.bills.downloadIconBillsScreen && message('Bills:download')}
      onClickDownload={onClickDownload}
      billId={appConfig.bills.billDownloadOnPDF ? cellData.external_id : cellData.id}
      period={cellData.period}
      amount={cellData.amount}
      disabled={!cellData.downloadable}
      className={styles.downloadButton}
    />
  </div>
);

const amountRenderer = ({ cellData }) =>
  cellData !== null && (
    <UTLabel small semibold>
      {numeral(cellData).format('$0,0.00')}
    </UTLabel>
  );

/* eslint-enable react/prop-types */
/* eslint-enable react/display-name */

export const columns = [
  {
    key: 'period',
    name: message('Bills:period').toUpperCase(),
    width: appConfig.billsColumns.period.width,
    order: appConfig.billsColumns.period.order,
    show: true,
    showInPayments: appConfig.payments.columns.period.show
  },
  {
    key: 'issued_on',
    name: message('Bill:issued_on').toUpperCase(),
    width: appConfig.billsColumns.issued_on.width,
    order: appConfig.billsColumns.issued_on.order,
    show: true,
    showInPayments: appConfig.payments.columns.issued_on.show
  },
  {
    key: 'document_type',
    name: message('Bills:documentType').toUpperCase(),
    width: appConfig.billsColumns.document_type.width,
    order: appConfig.billsColumns.document_type.order,
    show: true,
    showInPayments: appConfig.payments.columns.document_type.show
  },
  {
    key: 'document_number',
    name: message('Bill:document_number').toUpperCase(),
    width: appConfig.billsColumns.document_number.width,
    order: appConfig.billsColumns.document_number.order,
    show: true,
    showInPayments: appConfig.payments.columns.document_number.show
  },
  {
    key: 'first_expiration_on',
    name: message('Bills:expiration').toUpperCase(),
    width: appConfig.billsColumns.first_expiration_on.width,
    order: appConfig.billsColumns.first_expiration_on.order,
    show: true,
    showInPayments: appConfig.payments.columns.first_expiration_on.show
  },
  {
    key: 'status',
    name: message('Commons:status').toUpperCase(),
    width: appConfig.billsColumns.status.width,
    order: appConfig.billsColumns.status.order,
    show: true,
    showInPayments: appConfig.payments.columns.status.show
  },
  {
    key: 'amount',
    name: message('Bills:amount').toUpperCase(),
    width: appConfig.billsColumns.amount.width,
    order: appConfig.billsColumns.amount.order,
    cellRenderer: amountRenderer,
    show: true,
    showInPayments: appConfig.payments.columns.amount.show
  },
  {
    key: 'balance',
    name: message('Bills:balance').toUpperCase(),
    width: appConfig.billsColumns.balance.width,
    order: appConfig.billsColumns.balance.order,
    cellRenderer: amountRenderer,
    show: appConfig.useBillsBalance,
    showInPayments: appConfig.payments.columns.balance.show
  }
].sort((a, b) => (a.order < b.order ? -1 : 1));

function BillsScene({ bills, onClickDownload, onRowsRendered, appending, onSelectBill }) {
  return (
    <div style={stylesJS.table}>
      <DefaultViewport>
        <UTTable
          columns={[
            ...columns.filter(element => element.show),
            {
              key: 'download',
              name: message('Bills:download').toUpperCase(),
              width: appConfig.billsColumns.download.width,
              cellRenderer: downloadButtonRenderer(onClickDownload)
            }
          ].filter(item => item && item.key !== 'issued_on')}
          rows={bills}
          activeRowStyle={tableStyles.activeRowStyle}
          rowHeight={(index, activeRowIndex) => (index === activeRowIndex ? activeRowSize : inactiveRowSize)}
          openable
          renderActiveRow={(rowData, positionStyle) => (
            <BillDetails rowData={rowData} style={positionStyle} />
          )}
          id={BILLS}
          onRowsRendered={onRowsRendered}
          scrolleable
        />
      </DefaultViewport>
      <MobileViewport>
        <BillsMobileTable bills={bills} onRowsRendered={onRowsRendered} onSelectBill={onSelectBill} />
      </MobileViewport>
      {appending && (
        <div style={stylesJS.spinnerContainer}>
          <UTLoading loading={appending} size={45} text=" " />
        </div>
      )}
    </div>
  );
}

BillsScene.propTypes = {
  onClickDownload: func.isRequired,
  bills: any.isRequired, // eslint-disable-line react/forbid-prop-types
  onRowsRendered: func.isRequired,
  appending: bool,
  onSelectBill: func.isRequired
};

export default WithLoading(props => props.loading)(
  withErrorScreen(props => !props.bills.length)(Radium(BillsScene))
);
